import React, { memo, useMemo } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import {
  AccessorialDetailKind,
  AccessorialDetentionDetailsStored,
  AccessorialDetentionStopTimesSource,
  ShipmentWorkItemAccessorial
} from "../../../../app/data/shipment-reconciliation/models";
import { Routes } from "../../../../app/route/RoutesConfig";

export const isDetentionDetails = (d: unknown): d is AccessorialDetentionDetailsStored =>
  !!d && typeof d === "object" && "schemaVersion" in d && typeof (d as AccessorialDetentionDetailsStored).schemaVersion === "number";

const parseStopAndAllowanceMinutes = (reason: string): { stopMinutes: number | null; allowanceMinutes: number | null } => {
  const stopMatch = reason.match(/Stop duration of\s*(\d+)\s*minutes/i);
  const allowMatch = reason.match(/allowed unloading time of\s*(\d+)\s*minutes/i);
  return {
    stopMinutes: stopMatch ? parseInt(stopMatch[1], 10) : null,
    allowanceMinutes: allowMatch ? parseInt(allowMatch[1], 10) : null
  };
};

/** Legacy bucket labels when `detentionCategory` min/max are not on the payload. */
const formatWeightClassFromStopWeight = (weightLb: number | undefined | null): string => {
  if (weightLb == null || Number.isNaN(weightLb)) return "—";
  if (weightLb <= 2499) return "0 – 2,499 lbs";
  if (weightLb <= 4999) return "2,500 – 4,999 lbs";
  if (weightLb <= 9999) return "5,000 – 9,999 lbs";
  return "10,000+ lbs";
};

const formatDetentionWeightClass = (
  category: AccessorialDetentionDetailsStored["detentionCategory"],
  fallbackWeightLb: number | undefined | null
): string => {
  if (category && typeof category.minWeight === "number" && typeof category.maxWeight === "number") {
    const min = category.minWeight;
    const max = category.maxWeight;
    if (!Number.isNaN(min) && !Number.isNaN(max)) {
      return `${min.toLocaleString()} – ${max.toLocaleString()} lbs`;
    }
  }
  return formatWeightClassFromStopWeight(fallbackWeightLb);
};

const formatStopTimesSource = (source: AccessorialDetentionStopTimesSource | undefined): string => {
  if (source == null) return "—";
  switch (source) {
    case AccessorialDetentionStopTimesSource.MANUAL_POD:
      return "Manual POD";
    case AccessorialDetentionStopTimesSource.ONEXGS_DRIVER_APP:
      return "OneXGS Driver App";
    case AccessorialDetentionStopTimesSource.SAMSARA:
      return "Samsara";
    case AccessorialDetentionStopTimesSource.OTHER:
      return "Other";
    default:
      return String(source).replace(/_/g, " ");
  }
};

const formatStopDateTimeLocal = (value: string | null | undefined) =>
  value ? moment(value).format("MM/DD/YY · HH:mm") : "—";

const terminalPrefixedPath = (terminalNumber: number | undefined | null, route: string): string | null => {
  if (terminalNumber == null || Number.isNaN(Number(terminalNumber))) return null;
  return `/${terminalNumber}${route}`;
};

export interface OperationsReviewDetentionAccessorialDetailsProps {
  accessorial: ShipmentWorkItemAccessorial;
  probillNumber: number;
  shipmentWeight?: number | null;
  terminalNumber: number;
}

const OperationsReviewDetentionAccessorialDetails: React.FC<OperationsReviewDetentionAccessorialDetailsProps> = memo(
  ({ accessorial, probillNumber, shipmentWeight, terminalNumber }) => {
    const details =
      accessorial.detailKind === AccessorialDetailKind.DETENTION && isDetentionDetails(accessorial.details)
        ? accessorial.details
        : null;

    const stopProbills = useMemo(
      () => (details?.stopProbillNumbers?.length ? details.stopProbillNumbers : [probillNumber]),
      [details?.stopProbillNumbers, probillNumber]
    );

    if (!details) {
      return null;
    }

    const parsedMinutes = parseStopAndAllowanceMinutes(accessorial.applicationReason || "");
    const allowanceMinutes =
      details.detentionCategory?.freeTimeMinutes != null && !Number.isNaN(Number(details.detentionCategory.freeTimeMinutes))
        ? Number(details.detentionCategory.freeTimeMinutes)
        : parsedMinutes.allowanceMinutes ?? 15;

    const stopDurationMinutes =
      details.stopDurationMinutes != null && !Number.isNaN(Number(details.stopDurationMinutes))
        ? Number(details.stopDurationMinutes)
        : details.stopArrivedAt && details.stopDepartedAt
          ? Math.max(0, moment(details.stopDepartedAt).diff(moment(details.stopArrivedAt), "minutes"))
          : parsedMinutes.stopMinutes ?? 0;
    const excessMinutes = Math.max(0, (stopDurationMinutes || 0) - allowanceMinutes);
    const arrivedDisplay = formatStopDateTimeLocal(details.stopArrivedAt);
    const departedDisplay = formatStopDateTimeLocal(details.stopDepartedAt);
    const totalTimelineMinutes = Math.max(stopDurationMinutes || excessMinutes + allowanceMinutes, allowanceMinutes + excessMinutes, 1);
    const allowancePct = Math.min(100, (allowanceMinutes / totalTimelineMinutes) * 100);
    const excessPct = Math.max(0, 100 - allowancePct);

    const manifestDisplay =
      details.deliveryManifestNumber != null && !Number.isNaN(details.deliveryManifestNumber)
        ? String(details.deliveryManifestNumber)
        : "—";
    const stopNumDisplay =
      details.deliveryManifestStopNumber != null && !Number.isNaN(details.deliveryManifestStopNumber)
        ? String(details.deliveryManifestStopNumber)
        : "—";

    const totalStopWeightDisplay =
      details.stopWeight != null
        ? `${details.stopWeight.toLocaleString()} lbs`
        : shipmentWeight != null
          ? `${shipmentWeight.toLocaleString()} lbs`
          : "—";
    const probillWeightDisplay =
      details.probillWeight != null && !Number.isNaN(details.probillWeight)
        ? `${details.probillWeight.toLocaleString()} lbs`
        : "—";

    const driverName = details.driverName?.trim() ?? "";
    const driverId = details.driverId?.trim() ?? "";
    const driverLinkLabel = (() => {
      if (driverName && driverId) return `${driverName} (${driverId})`;
      if (driverName) return driverName;
      if (driverId) return driverId;
      return "—";
    })();

    const manifestBasePath = terminalPrefixedPath(terminalNumber, Routes.manifest.manifests);
    const manifestHref =
      manifestDisplay !== "—" && manifestBasePath
        ? `${manifestBasePath}?manifestNumber=${encodeURIComponent(manifestDisplay)}`
        : null;

    const driversBasePath = terminalPrefixedPath(terminalNumber, Routes.driversList.list);
    const driverSearchQuery = driverId || driverName;
    const driverHref =
      driverSearchQuery && driversBasePath ? `${driversBasePath}?search=${encodeURIComponent(driverSearchQuery)}` : null;

    const probillsColumn = (
      <>
        <div className="xgs-operations-review-item-details-v2__muted">Probills</div>
        <div className="xgs-operations-review-item-details-v2__probills-row">
          {stopProbills.map((n, i) => (
            <React.Fragment key={n}>
              {i > 0 && <span>, </span>}
              <Link className="xgs-operations-review-item-details-v2__probill-link" to={`/shipments/${n}`} target="_blank" rel="noopener noreferrer">
                {n}
              </Link>
            </React.Fragment>
          ))}
        </div>
      </>
    );

    return (
      <div className="xgs-or-accessorials-section__detention-details">
        <h3 className="xgs-operations-review-item-details-v2__section-title">Delivery Stop Overview</h3>
        <div className="xgs-operations-review-item-details-v2__panel">
          <div className="xgs-operations-review-item-details-v2__subline xgs-operations-review-item-details-v2__detention-panel-line">
            {manifestHref ? (
              <Link className="xgs-operations-review-item-details-v2__probill-link" to={manifestHref} target="_blank" rel="noopener noreferrer">
                {`Manifest #${manifestDisplay}`}
              </Link>
            ) : (
              <span>{`Manifest #${manifestDisplay}`}</span>
            )}
            <span className="xgs-operations-review-item-details-v2__subline-dot" aria-hidden />
            <span>{`Stop #${stopNumDisplay}`}</span>
            <span className="xgs-operations-review-item-details-v2__subline-dot" aria-hidden />
            {driverHref ? (
              <Link className="xgs-operations-review-item-details-v2__probill-link" to={driverHref} target="_blank" rel="noopener noreferrer">
                {`Driver: ${driverLinkLabel}`}
              </Link>
            ) : (
              <span>{`Driver: ${driverLinkLabel}`}</span>
            )}
          </div>

          <div className="xgs-operations-review-item-details-v2__detention-overview-columns">
            <div className="xgs-operations-review-item-details-v2__detention-overview-left">
              <div className="xgs-operations-review-item-details-v2__subline xgs-operations-review-item-details-v2__detention-panel-line xgs-operations-review-item-details-v2__detention-panel-line--tight">
                <span>{`Stop weight: ${totalStopWeightDisplay}`}</span>
                <span className="xgs-operations-review-item-details-v2__subline-dot" aria-hidden />
                <span>{`Probill weight: ${probillWeightDisplay}`}</span>
              </div>
              <div className="xgs-operations-review-item-details-v2__subline xgs-operations-review-item-details-v2__detention-panel-line xgs-operations-review-item-details-v2__detention-panel-line--tight">
                <span>{`Weight class: ${formatDetentionWeightClass(details.detentionCategory, details.stopWeight ?? shipmentWeight)}`}</span>
                <span className="xgs-operations-review-item-details-v2__subline-dot" aria-hidden />
                <span>{`Allowed free time: ${allowanceMinutes} min`}</span>
              </div>
            </div>
            <div className="xgs-operations-review-item-details-v2__detention-overview-divider" aria-hidden />
            <div className="xgs-operations-review-item-details-v2__detention-overview-right">{probillsColumn}</div>
          </div>
        </div>

        <h3 className="xgs-operations-review-item-details-v2__section-title">Stop Time Analysis</h3>
        <div className="xgs-operations-review-item-details-v2__panel">
          <div className="xgs-operations-review-item-details-v2__stop-grid">
            <div className="xgs-operations-review-item-details-v2__stop-col">
              <div className="xgs-operations-review-item-details-v2__muted">Arrived</div>
              <div>{arrivedDisplay}</div>
            </div>
            <div className="xgs-operations-review-item-details-v2__stop-col xgs-operations-review-item-details-v2__stop-col--right">
              <div className="xgs-operations-review-item-details-v2__muted">Departed</div>
              <div>{departedDisplay}</div>
            </div>
          </div>
          <div className="xgs-operations-review-item-details-v2__timeline-bar">
            <div
              className={`xgs-operations-review-item-details-v2__timeline-seg xgs-operations-review-item-details-v2__timeline-seg--allow`}
              style={{ width: `${allowancePct}%` }}
            >
              {allowanceMinutes > 0 ? `${allowanceMinutes} min` : ""}
            </div>
            <div
              className={`xgs-operations-review-item-details-v2__timeline-seg xgs-operations-review-item-details-v2__timeline-seg--excess`}
              style={{ width: `${excessPct}%` }}
            >
              {excessMinutes > 0 ? `${excessMinutes} min excess` : ""}
            </div>
          </div>
          <div className="xgs-operations-review-item-details-v2__stop-time-meta-row">
            <div className="xgs-operations-review-item-details-v2__stop-time-meta-main">
              <div className="xgs-operations-review-item-details-v2__legend-item">
                <span className="xgs-operations-review-item-details-v2__legend-sq xgs-operations-review-item-details-v2__legend-sq--allow" />
                Allowance ({allowanceMinutes} min)
              </div>
              <span className="xgs-operations-review-item-details-v2__stop-time-meta-sep" aria-hidden>
                ·
              </span>
              <div className="xgs-operations-review-item-details-v2__legend-item">
                <span className="xgs-operations-review-item-details-v2__legend-sq xgs-operations-review-item-details-v2__legend-sq--excess" />
                Potential Detention ({excessMinutes} min)
              </div>
            </div>
            <span className="xgs-operations-review-item-details-v2__stop-time-meta-source">
              Source: {formatStopTimesSource(details.stopTimesSource)}
            </span>
          </div>
        </div>
      </div>
    );
  }
);

export default OperationsReviewDetentionAccessorialDetails;
